import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import ColorButton from 'components/Widget/Button/ColorButton';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectOrganizations, selectUser, selectUserSettings } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { displayDsShowcaseModal } from 'redux/mode/util/modals';
import * as routes from 'routes';
import * as Base from '../styles';
import * as S from './styles';

const DesignSystemCreator = () => {
	const navigate = useNavigate();
	const [loadingMessage, setLoadingMessage] = useState<string>('');

	const { organizations, defaultOrganizationId, user } = useSelector(
		(r: RootReducer) => ({
			organizations: selectOrganizations(r.db),
			defaultOrganizationId: selectUserSettings(r.db).defaultOrganizationId,
			user: selectUser(r.db),
		}),
		shallowEqual
	);

	// Clear stale default org if it was deleted
	useEffect(() => {
		if (!defaultOrganizationId || !user || user.id === 'guest') return;
		if (organizations.length === 0) return;
		const orgExists = organizations.some((org) => org.id === defaultOrganizationId);
		if (!orgExists) {
			forge
				.updateUser({
					update: {
						id: user.id,
						metadata: {
							settings: { ...user.metadata?.settings, defaultOrganizationId: null },
						},
					},
				})
				.catch((err) => console.error('Failed to clear stale defaultOrganizationId:', err));
		}
	}, [organizations, defaultOrganizationId, user]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	useEffect(() => {
		trackDesignSystemAction('view_create_page');
	}, []);

	const onCreateBlankDs = async () => {
		try {
			setLoadingMessage('creating design system...');
			const res = await forge.createDesignSystem.withToast()({
				organizationId: defaultOrganizationId || undefined,
			});
			trackDesignSystemAction('created_blank_ds');
			setLoadingMessage('');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title) + '?new=true');
		} catch {
			setLoadingMessage('');
		}
	};

	const onBrowseGallery = () => {
		trackDesignSystemAction('click_browse_gallery');
		displayDsShowcaseModal({
			mode: 'view',
			onSelect: (item) => navigate(item.dsUrl),
		});
	};

	const onStartFromScratch = () => {
		trackDesignSystemAction('click_start_from_scratch');
		onCreateBlankDs();
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<Base.Base>
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<Base.Content>
				<S.PageContent>
					<S.Headline>Set up your design system</S.Headline>
					<S.Subheading>
						Every design system is different. Pick the path that fits your team.
					</S.Subheading>

					<S.CardGrid>
						<S.Card>
							<S.CardLabel>Easy mode</S.CardLabel>
							<S.CardTitle>Modify a starter kit</S.CardTitle>
							<S.CardDescription>
								Start with one of our prebuilt design systems and modify it to your needs. Chat with
								the Moonchild agent to customize it to your needs. Change colors, import components,
								and more.
							</S.CardDescription>
							<S.ButtonRow>
								<ColorButton
									label="Browse gallery"
									onClick={onBrowseGallery}
									data-testid="ds-browse-gallery-button"
								/>
							</S.ButtonRow>
						</S.Card>

						<S.Card>
							<S.CardLabel>For the brave</S.CardLabel>
							<S.CardTitle>Build it yourself</S.CardTitle>
							<S.CardDescription>
								Use the Moonchild agent to import your design system or create one from scratch.
								GitHub repos and Figma files are supported. It's powerful and getting better fast.
							</S.CardDescription>
							<S.ButtonRow>
								<ColorButton
									label="Start from scratch"
									onClick={onStartFromScratch}
									disabled={!!loadingMessage}
									data-testid="ds-start-from-scratch-button"
								/>
							</S.ButtonRow>
						</S.Card>
					</S.CardGrid>
				</S.PageContent>
			</Base.Content>
		</Base.Base>
	);
};

export default DesignSystemCreator;
